/**
 * Change Password Modal - 修改自己的密碼
 */
import { Modal, Form, Input, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import userService, { type ChangePasswordRequest } from '@/api/services/userService';

interface ChangePasswordModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

const ChangePasswordModal = ({ open, onCancel, onSuccess }: ChangePasswordModalProps) => {
  const { t } = useTranslation(['password', 'common', 'validation']);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const data: ChangePasswordRequest = {
        new_password: values.new_password,
        confirm_password: values.confirm_password,
      };

      await userService.changeMyPassword(data);

      message.success(t('password:messages.changeSuccess'));
      form.resetFields();
      onSuccess?.();
      onCancel();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          message.error(detail);
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          const errors = detail.map((err: any) => err.msg).join(', ');
          message.error(errors);
        }
      } else {
        message.error(t('password:messages.changeFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t('password:changePassword')}
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={t('password:confirmChange')}
      cancelText={t('common:actions.cancel')}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          name="new_password"
          label={t('password:fields.newPassword')}
          rules={[
            { required: true, message: t('validation:required', { field: t('password:fields.newPassword') }) },
            { min: 8, message: t('validation:minLength', { min: 8 }) },
          ]}
        >
          <Input.Password
            placeholder={t('password:placeholders.newPassword')}
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label={t('password:fields.confirmNewPassword')}
          dependencies={['new_password']}
          rules={[
            { required: true, message: t('validation:required', { field: t('password:fields.confirmNewPassword') }) },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('validation:passwordMismatch')));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={t('password:placeholders.confirmNewPassword')}
            autoComplete="new-password"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
